/**
 * Pure indexing/grouping helpers over artifact.json (docs/specs/app.md, "artifact.json — the
 * viewer contract"). No fetching, no React — kept separate so it's unit-testable without a DOM.
 */
import type {
  Artifact,
  ArtifactFeature,
  ArtifactProposedEntry,
  ArtifactSubfeature,
  ArtifactVendor,
  Axis,
  Cell,
} from '../types.ts';

/** Rows = axes, user axes before market axes, stable within each group (spec: "apps/web" §3). */
export function orderedAxes(axes: Axis[]): Axis[] {
  const user = axes.filter((a) => a.source === 'user');
  const market = axes.filter((a) => a.source === 'market');
  return [...user, ...market];
}

/** The one cell for a given axis x vendor, if the matrix has it. */
export function cellFor(matrix: Artifact['matrix'], axisCapability: string, company: string): Cell | null {
  return matrix.cells.find((c) => c.axis === axisCapability && c.company === company) ?? null;
}

export function vendorByCompany(artifact: Artifact, company: string): ArtifactVendor | null {
  return artifact.vendors.find((v) => v.company === company) ?? null;
}

export interface ResolvedCellEntry {
  feature: ArtifactFeature;
  subfeature: ArtifactSubfeature | null;
}

/**
 * Resolve a cell's citation against a vendor's feature list. Matches on (product, kind, entry
 * name, parent) per the spec's anchor-precision note — name alone is ambiguous across products.
 * Returns null when the cell cites nothing (verdicts like `absent`/`unknown` commonly have no
 * entry) or when the citation doesn't resolve against this vendor's features.
 *
 * Takes only the anchor fields, not a whole Cell, so the demand panel's friction anchors
 * (lib/demand.ts) resolve through this same convention instead of growing a second one.
 */
export type EntryAnchor = Pick<Cell, 'entry' | 'kind' | 'parent' | 'product'>;

export function resolveCellEntry(vendor: ArtifactVendor, cell: EntryAnchor): ResolvedCellEntry | null {
  if (!cell.entry) return null;
  if (cell.kind === 'subfeature') {
    for (const feature of vendor.features) {
      if (cell.product && feature.product !== cell.product) continue;
      if (cell.parent && feature.name !== cell.parent) continue;
      const subfeature = feature.subfeatures.find((s) => s.name === cell.entry);
      if (subfeature) return { feature, subfeature };
    }
    return null;
  }
  const feature = vendor.features.find(
    (f) => f.name === cell.entry && (!cell.product || f.product === cell.product),
  );
  return feature ? { feature, subfeature: null } : null;
}

export interface VendorFeatureSplit {
  compared: ArtifactFeature[];
  notCompared: ArtifactFeature[];
}

/** "Not compared" is `compared: false` verbatim — no re-judging, per the spec. */
export function splitVendorFeatures(vendor: ArtifactVendor): VendorFeatureSplit {
  const compared: ArtifactFeature[] = [];
  const notCompared: ArtifactFeature[] = [];
  for (const f of vendor.features) {
    (f.compared ? compared : notCompared).push(f);
  }
  return { compared, notCompared };
}

/**
 * `cost_usd` is `number | null` — untracked for stage-by-stage corpus builds (the sample
 * artifact has null for all four vendors). Never render null as $0.00, which would claim a
 * known figure that isn't one. Absent reads the same way: the server removes the field for a
 * non-admin, and although `showCost` keeps this from being called for them, it must not throw.
 */
export function formatCost(costUsd: number | null | undefined): string {
  return costUsd == null ? '—' : `$${costUsd.toFixed(2)}`;
}

export interface ProposedGroup {
  product: string;
  names: string[];
}

/**
 * Groups proposed-only names by product, first-seen order. The same name (e.g. "JSON") can be
 * proposed under several products in one vendor's record — a flat name list reads as a
 * duplicate-looking bug, so the "not compared" view shows product context (real sample data:
 * brightdata proposes "JSON" separately under three products).
 */
export function groupProposedByProduct(proposed: ArtifactProposedEntry[]): ProposedGroup[] {
  const order: string[] = [];
  const byProduct = new Map<string, string[]>();
  for (const p of proposed) {
    if (!byProduct.has(p.product)) {
      byProduct.set(p.product, []);
      order.push(p.product);
    }
    byProduct.get(p.product)!.push(p.name);
  }
  return order.map((product) => ({ product, names: byProduct.get(product)! }));
}
